import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import CarouselDotIcon from "icons/CarouselDotIcon";
import DotIcon from "icons/DotIcon";
import HeartFillIcon from "icons/HeartFillIcon";
import React from "react";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./CarouselIndicator.module.scss";

interface Props {
  activeDot: number;
  totalSlider: number;
  dotColor?: string;
  activeDotColor?: string;
  activeDotClassName?: string;
  dotClassName?: string;
  handleClickIndicator: (index: number) => void;
  type?: string;
  theme?: string;
  indicatorWrapperClassName?: any;
  smallHeight?: boolean;
  addInfinite?: boolean;
  maxDots?: number;
  dataTestId?: string;
  /**
   * Turns the active `line` dot into a progress bar filling over this many ms. Omit for the
   * plain dot — every other caller gets exactly what it got before.
   */
  progressMs?: number;
}

const CarouselIndicator: React.FC<Props> = (props) => {
  const {
    activeDot,
    totalSlider,
    activeDotColor,
    dotColor = "#D1D5DB",
    handleClickIndicator,
    type,
    theme,
    indicatorWrapperClassName,
    smallHeight,
    activeDotClassName,
    dotClassName,
    addInfinite,
    maxDots = 5,
    dataTestId,
    progressMs,
  } = props;

  // Nothing to indicate when there is only a single (or zero) slide.
  if (!totalSlider || totalSlider <= 1) return null;

  const getTheme = () => {
    switch (theme) {
      case "secondary":
        return styles.secondary;
      default:
        return null;
    }
  };

  const generateCarouselPoint = (index: any) => {
    if (type === "line") {
      const isActive = activeDot === index + 1;
      const showProgress = isActive && Boolean(progressMs);
      return (
        <div
          data-testid={dataTestId}
          key={index}
          className={formatClassNames(
            styles.line,
            isActive && styles.active,
            getTheme(),
            smallHeight && styles.smallHeight,
            showProgress && styles.lineTrack,
          )}
          style={{
            // While filling, the dot itself is the unfilled track — the active colour is
            // carried by the fill rather than the whole dot.
            backgroundColor: showProgress || !isActive ? dotColor : activeDotColor,
          }}
        >
          {showProgress && (
            // Keyed on the slide so advancing restarts the fill rather than resuming it.
            <span
              key={activeDot}
              className={styles.lineProgress}
              style={{ backgroundColor: activeDotColor, animationDuration: `${progressMs}ms` }}
            />
          )}
        </div>
      );
    }
    if (type === "heart") {
      return (
        <div
          data-testid={dataTestId}
          role="none"
          key={index}
          onClick={() => handleClickIndicator(index + 1)}
          className={activeDot === index + 1 ? styles.activeHeart : styles.heart}
        >
          <HeartFillIcon fill={activeDot === index + 1 ? activeDotColor : dotColor} />
        </div>
      );
    }
    if (type === "circle") {
      return (
        <div
          data-testid={dataTestId}
          role="none"
          key={index}
          onClick={() => handleClickIndicator(index + 1)}
          className={formatClassNames(
            styles.circle,
            activeDot === index + 1 && activeDotClassName,
            dotClassName,
          )}
        >
          <DotIcon width="8px" fill={activeDot === index + 1 ? activeDotColor : dotColor} />
        </div>
      );
    }
    return (
      <div
        data-testid={dataTestId}
        key={index}
        role="none"
        onClick={() => handleClickIndicator(index + 1)}
        className={activeDot === index + 1 ? styles.activeDot : styles.dot}
      >
        <CarouselDotIcon fill={activeDot === index + 1 ? activeDotColor : dotColor} />
      </div>
    );
  };

  return (
    <div
      data-testid="property-carousel-indicator"
      className={formatClassNames(
        styles.carouselIdicatorWrap,
        type && styles[`${type}Wrap`],
        indicatorWrapperClassName && indicatorWrapperClassName,
        addInfinite && styles.addInfinite,
      )}
      style={
        addInfinite
          ? {
              width: `${maxDots * 15 + 1}px`,
            }
          : {}
      }
    >
      {(() => {
        // Each dot slot in the content flow occupies ~12px (6px dot + 6px
        // gap). Use that as the pitch and clamp the shift so the tail of the
        // track stays inside the visible window on the last few slides,
        // instead of scrolling the dots off-screen to the left.
        const DOT_PITCH = 12;
        const centerCount = Math.floor(maxDots / 2) + 1;
        const rawShift = DOT_PITCH * Math.max(0, activeDot - centerCount);
        const maxShift = Math.max(0, totalSlider - maxDots) * DOT_PITCH;
        const clampedShift = Math.min(rawShift, maxShift);
        return (
          <div
            className={styles.contentWrap}
            style={addInfinite ? { transform: `translateX(-${clampedShift}px)` } : {}}
          >
            {[...Array(totalSlider)].map((_it, index) => generateCarouselPoint(index))}
          </div>
        );
      })()}
    </div>
  );
};

CarouselIndicator.defaultProps = {
  dotColor: undefined,
  activeDotColor: undefined,
  activeDotClassName: "",
  dotClassName: "",
  type: undefined,
  theme: undefined,
  indicatorWrapperClassName: "",
  smallHeight: false,
  addInfinite: false,
  maxDots: 5,
  progressMs: undefined,
};

export default wrapperHOC(CarouselIndicator, {
  componentName: "CarouselIndicator-Carousel-Global",
  showForChina: true,
});
